import { useState } from "react";
import type { HTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  /** URL da imagem. Se faltar ou falhar, cai nas iniciais. */
  src?: string | null;
  /** Nome completo — vira iniciais e texto alternativo. */
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** `circle` = pessoa; `square` = entidade (grupo, campanha) em linha de tabela. */
  shape?: "circle" | "square";
}

const SIZES: Record<NonNullable<AvatarProps["size"]>, string> = {
  // 9px: única exceção abaixo de 11px, e só dentro de um círculo de 20px
  xs: "size-5 text-[0.5625rem]",
  sm: "size-6 text-2xs",
  md: "size-8 text-xs",
  lg: "size-10 text-sm",
  xl: "size-12 text-base",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase();
}

/**
 * Pessoa = círculo com iniciais em violeta suave; entidade = quadrado de
 * raio 8 em cinza (DESIGN.md › Components › avatar).
 */
export function Avatar({ src, name, size = "md", shape = "circle", className, ...props }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-semibold tracking-wide",
        shape === "circle"
          ? "rounded-full bg-primary-soft text-primary-text"
          : "rounded-md bg-muted text-muted-foreground",
        SIZES[size],
        className,
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src as string}
          alt={name}
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <>
          <span aria-hidden="true">{initials(name)}</span>
          <span className="sr-only">{name}</span>
        </>
      )}
    </span>
  );
}
